class CbnMobileMenu extends HTMLElement {
  #mobileMedia = window.matchMedia('(max-width: 989px)');
  #activeScreen = 'main';
  #hydrated = false;
  #returnFocus = null;
  #menuOpener = null;
  #menuOpenerTabIndex = null;
  #menuOpenerHadTabIndex = false;
  #menuOpenerDisabled = false;
  #backgroundState = [];
  #drawerA11yState = null;
  #controller = new AbortController();
  #primingController = new AbortController();
  #contentObserver = new MutationObserver(() => {
    if (!this.#mobileMedia.matches) return;
    if (this.querySelector(':scope > [data-cbn-screen="main"]')) return;

    const drawerDetails = this.closest('details');
    if (!(drawerDetails instanceof HTMLDetailsElement) || !drawerDetails.open) return;

    queueMicrotask(() => this.#hydrate());
  });

  connectedCallback() {
    this.#mobileMedia.addEventListener('change', this.#handleViewportChange);
    this.#contentObserver.observe(this, { childList: true });
    this.#bindHydrationTriggers();

    const drawerDetails = this.closest('details');
    if (this.#mobileMedia.matches && drawerDetails instanceof HTMLDetailsElement && drawerDetails.open) {
      this.#hydrate();
    }
  }

  disconnectedCallback() {
    this.#mobileMedia.removeEventListener('change', this.#handleViewportChange);
    this.#contentObserver.disconnect();
    this.#primingController.abort();
    this.#teardownInteractions();
  }

  #handleViewportChange = (event) => {
    if (!event.matches) return;

    const drawerDetails = this.closest('details');
    if (drawerDetails instanceof HTMLDetailsElement && drawerDetails.open) this.#hydrate();
  };

  #bindHydrationTriggers() {
    if (this.#primingController.signal.aborted) this.#primingController = new AbortController();
    const { signal } = this.#primingController;
    const drawerDetails = this.closest('details');
    const menuOpener = drawerDetails?.querySelector(':scope > summary');
    if (!(drawerDetails instanceof HTMLDetailsElement) || !(menuOpener instanceof HTMLElement)) return;

    const hydrate = () => this.#hydrate();
    menuOpener.addEventListener('pointerdown', hydrate, { signal, once: true, passive: true });
    menuOpener.addEventListener('focusin', hydrate, { signal, once: true });
    menuOpener.addEventListener('click', hydrate, { signal, once: true, capture: true });
    drawerDetails.addEventListener('toggle', this.#handlePreHydrationToggle, { signal });
  }

  #handlePreHydrationToggle = (event) => {
    const drawerDetails = event.currentTarget;
    if (drawerDetails instanceof HTMLDetailsElement && drawerDetails.open) this.#hydrate();
  };

  #hydrate() {
    const activeMainScreen = this.querySelector(':scope > [data-cbn-screen="main"]');
    if (activeMainScreen) {
      this.#hydrated = true;
      this.dataset.cbnHydrated = 'true';
      if (this.#controller.signal.aborted) this.#bindInteractions();
      return;
    }

    const template = this.querySelector(':scope > template[data-cbn-mobile-template]');
    if (!(template instanceof HTMLTemplateElement)) return;

    this.append(template.content.cloneNode(true));
    this.#hydrated = true;
    this.dataset.cbnHydrated = 'true';
    this.#bindInteractions();
  }

  #bindInteractions() {
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.querySelectorAll('[data-cbn-open-screen]').forEach((trigger) => {
      trigger.addEventListener('click', this.#handleOpen, { signal });
    });

    this.querySelectorAll('[data-cbn-back]').forEach((trigger) => {
      trigger.addEventListener('click', this.#handleBack, { signal });
    });

    this.querySelectorAll('[data-cbn-accordion]').forEach((details) => {
      details.addEventListener('toggle', this.#handleAccordion, { signal });
    });

    const drawerDetails = this.closest('details');
    this.#menuOpener = drawerDetails?.querySelector(':scope > summary') ?? null;
    drawerDetails?.addEventListener('toggle', this.#handleDrawerToggle, { signal });
    drawerDetails?.addEventListener('keydown', this.#handleDrawerKeydown, { signal });

    this.#setScreen('main', false);

    if (drawerDetails instanceof HTMLDetailsElement && drawerDetails.open) {
      this.#activateDrawer(drawerDetails);
    }
  }

  #teardownInteractions() {
    this.#restoreMenuOpener();
    this.#releaseBackground();
    this.#controller.abort();
  }

  #handleOpen = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    const targetScreen = trigger.dataset.cbnOpenScreen;
    if (!targetScreen) return;

    event.preventDefault();
    this.#returnFocus = trigger;
    this.#setScreen(targetScreen, true);
  };

  #handleBack = () => {
    this.#setScreen('main', true);

    if (this.#returnFocus instanceof HTMLElement) {
      requestAnimationFrame(() => this.#returnFocus?.focus());
    }
  };

  #handleAccordion = (event) => {
    const openedDetails = event.currentTarget;
    if (!(openedDetails instanceof HTMLDetailsElement)) return;

    if (!openedDetails.open) return;

    this.querySelectorAll('[data-cbn-accordion][open]').forEach((details) => {
      if (details !== openedDetails && details instanceof HTMLDetailsElement) details.open = false;
    });
  };

  #handleDrawerToggle = (event) => {
    const drawerDetails = event.currentTarget;
    if (!(drawerDetails instanceof HTMLDetailsElement)) return;

    if (drawerDetails.open) {
      this.#activateDrawer(drawerDetails);
      return;
    }

    this.#restoreMenuOpener();
    this.#releaseBackground();
    this.#setScreen('main', false);

    if (this.#menuOpener instanceof HTMLElement) {
      requestAnimationFrame(() => this.#menuOpener?.focus({ preventScroll: true }));
    }
  };

  #handleDrawerKeydown = (event) => {
    if (event.key !== 'Tab') return;

    const drawerDetails = event.currentTarget;
    if (!(drawerDetails instanceof HTMLDetailsElement) || !drawerDetails.open) return;

    const focusableElements = this.#getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey && (activeElement === firstElement || !focusableElements.includes(activeElement))) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && (activeElement === lastElement || !focusableElements.includes(activeElement))) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  #activateDrawer(drawerDetails) {
    this.#isolateBackground(drawerDetails);
    this.#disableMenuOpener();

    const focusDrawerEntry = () => {
      if (!drawerDetails.open) return;

      const drawer = this.closest('.menu-drawer');
      if (drawer instanceof HTMLElement && drawer.contains(document.activeElement)) return;

      const activeScreen = this.querySelector(`[data-cbn-screen="${this.#activeScreen}"]`);
      const focusTarget =
        activeScreen?.querySelector('[data-cbn-back]') ?? drawer?.querySelector(':scope > .menu-drawer__close-button');

      if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    };

    requestAnimationFrame(() => requestAnimationFrame(focusDrawerEntry));
    window.setTimeout(focusDrawerEntry, 220);
  }

  #disableMenuOpener() {
    if (!(this.#menuOpener instanceof HTMLElement) || this.#menuOpener.getAttribute('tabindex') === '-1') return;

    this.#menuOpenerHadTabIndex = this.#menuOpener.hasAttribute('tabindex');
    this.#menuOpenerTabIndex = this.#menuOpener.getAttribute('tabindex');
    this.#menuOpener.setAttribute('tabindex', '-1');
    this.#menuOpenerDisabled = true;
  }

  #restoreMenuOpener() {
    if (!(this.#menuOpener instanceof HTMLElement) || !this.#menuOpenerDisabled) return;

    if (this.#menuOpenerHadTabIndex && this.#menuOpenerTabIndex !== null) {
      this.#menuOpener.setAttribute('tabindex', this.#menuOpenerTabIndex);
    } else {
      this.#menuOpener.removeAttribute('tabindex');
    }

    this.#menuOpenerHadTabIndex = false;
    this.#menuOpenerTabIndex = null;
    this.#menuOpenerDisabled = false;
  }

  #getFocusableElements() {
    const drawer = this.closest('.menu-drawer');
    if (!(drawer instanceof HTMLElement)) return [];

    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'summary',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(drawer.querySelectorAll(selector)).filter((element) => {
      if (!(element instanceof HTMLElement) || element.tabIndex < 0) return false;
      if (element.closest('[inert], [aria-hidden="true"]')) return false;

      const styles = window.getComputedStyle(element);
      return styles.display !== 'none' && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
    });
  }

  #isolateBackground(drawerDetails) {
    if (this.#backgroundState.length > 0) return;

    let currentElement = drawerDetails;
    while (currentElement.parentElement) {
      const parentElement = currentElement.parentElement;

      Array.from(parentElement.children).forEach((sibling) => {
        if (sibling === currentElement || !(sibling instanceof HTMLElement)) return;

        this.#backgroundState.push({
          element: sibling,
          hadInert: sibling.hasAttribute('inert'),
          ariaHidden: sibling.getAttribute('aria-hidden'),
        });

        sibling.setAttribute('inert', '');
        sibling.setAttribute('aria-hidden', 'true');
      });

      currentElement = parentElement;
      if (parentElement === document.body) break;
    }

    const drawer = this.closest('.menu-drawer');
    if (!drawer) return;

    this.#drawerA11yState = {
      element: drawer,
      role: drawer.getAttribute('role'),
      ariaModal: drawer.getAttribute('aria-modal'),
      ariaLabel: drawer.getAttribute('aria-label'),
    };

    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', this.dataset.cbnDialogLabel || 'Navigation');
  }

  #releaseBackground() {
    this.#backgroundState.reverse().forEach(({ element, hadInert, ariaHidden }) => {
      if (!hadInert) element.removeAttribute('inert');
      if (ariaHidden === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', ariaHidden);
    });
    this.#backgroundState = [];

    if (!this.#drawerA11yState) return;

    const { element, role, ariaModal, ariaLabel } = this.#drawerA11yState;
    if (role === null) element.removeAttribute('role');
    else element.setAttribute('role', role);
    if (ariaModal === null) element.removeAttribute('aria-modal');
    else element.setAttribute('aria-modal', ariaModal);
    if (ariaLabel === null) element.removeAttribute('aria-label');
    else element.setAttribute('aria-label', ariaLabel);
    this.#drawerA11yState = null;
  }

  #setScreen(screenName, moveFocus) {
    this.#activeScreen = screenName;
    this.dataset.screen = screenName;

    if (screenName === 'pbn' && !this.querySelector('[data-cbn-accordion][open]')) {
      const themesAccordion = this.querySelector('.cbn-mobile-menu__accordion--themes');
      if (themesAccordion instanceof HTMLDetailsElement) themesAccordion.open = true;
    }

    const drawer = this.closest('.menu-drawer');
    drawer?.classList.toggle('cbn-mobile-menu--subscreen-open', screenName !== 'main');
    if (drawer instanceof HTMLElement) drawer.scrollLeft = 0;
    this.scrollLeft = 0;

    this.querySelectorAll('[data-cbn-screen]').forEach((screen) => {
      if (!(screen instanceof HTMLElement)) return;

      const isActive = screen.dataset.cbnScreen === screenName;
      screen.toggleAttribute('inert', !isActive);
      screen.setAttribute('aria-hidden', String(!isActive));
    });

    this.querySelectorAll('[data-cbn-open-screen]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      trigger.setAttribute('aria-expanded', String(trigger.dataset.cbnOpenScreen === screenName));
    });

    if (!moveFocus || screenName === 'main') return;

    const activeScreen = this.querySelector(`[data-cbn-screen="${screenName}"]`);
    const focusTarget = activeScreen?.querySelector('[data-cbn-back]');
    if (focusTarget instanceof HTMLElement) requestAnimationFrame(() => focusTarget.focus());
  }
}

if (!customElements.get('cbn-mobile-menu')) {
  customElements.define('cbn-mobile-menu', CbnMobileMenu);
}
